import { useState, useEffect } from "react";
import AppLogo from "./AppLogo";
import { C, FONT } from "./ui/theme";
import { TextField, PasswordField, Button, ValidationMessage, FormKitStyles } from "./ui/FormKit";
import { trialSignup } from "./api";

const VALUE_POINTS = [
  "Account Intelligence across every company you target",
  "AI-powered company research, done in seconds",
  "Decision-maker identification, automatically",
  "Real-time buying signals so you reach out at the right moment",
  "AI personalization for every message you send",
  "Campaigns & sequences that run themselves",
  "Email engagement tracking on every send",
  "Analytics that show what's actually working",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(fields) {
  const e = {};
  if (!fields.firstName.trim()) e.firstName = "First name is required";
  if (!fields.lastName.trim()) e.lastName = "Last name is required";
  if (!fields.email.trim()) e.email = "Work email is required";
  else if (!EMAIL_RE.test(fields.email.trim())) e.email = "Enter a valid email address";
  if (!fields.companyName.trim()) e.companyName = "Company name is required";
  if (!fields.password) e.password = "Password is required";
  else if (fields.password.length < 8) e.password = "Must be at least 8 characters";
  else if (!/[A-Za-z]/.test(fields.password) || !/[0-9]/.test(fields.password)) e.password = "Must include a letter and a number";
  if (!fields.confirmPassword) e.confirmPassword = "Please confirm your password";
  else if (fields.confirmPassword !== fields.password) e.confirmPassword = "Passwords don't match";
  return e;
}

export default function TrialSignupPage({ onSignedUp, onBack, onLogin }) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [fields, setFields] = useState({
    firstName: "", lastName: "", email: "", companyName: "", jobTitle: "",
    password: "", confirmPassword: "", companyWebsite: "",
  });
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setFields(f => ({ ...f, [k]: e.target.value }));
  const blur = (k) => () => { setTouched(t => ({ ...t, [k]: true })); setErrors(validate(fields)); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    const v = validate(fields);
    setErrors(v);
    setTouched({ firstName: true, lastName: true, email: true, companyName: true, password: true, confirmPassword: true });
    if (Object.keys(v).length > 0) return;
    setSubmitting(true);
    try {
      const res = await trialSignup({
        firstName: fields.firstName.trim(),
        lastName: fields.lastName.trim(),
        email: fields.email.trim(),
        companyName: fields.companyName.trim(),
        jobTitle: fields.jobTitle.trim() || undefined,
        password: fields.password,
        confirmPassword: fields.confirmPassword,
        companyWebsite: fields.companyWebsite.trim() || undefined,
      });
      onSignedUp(res.user, res.token, res.organization);
    } catch (err) {
      // request() surfaces the server's { error } message directly — these
      // map to the exact cases called out in the spec: existing account,
      // weak password, mismatch, missing fields, rate limiting, and a
      // generic fallback for anything else (network drop, 500, etc).
      const msg = err?.message || "";
      if (/already exists/i.test(msg)) setServerError(msg);
      else if (/too many/i.test(msg)) setServerError(msg);
      else if (msg && msg !== "Failed to fetch") setServerError(msg);
      else setServerError("We couldn't reach the server. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const err = (k) => (touched[k] ? errors[k] : undefined);

  return (
    <div style={{ fontFamily: FONT, minHeight: "100vh", display: "flex", flexDirection: isMobile ? "column" : "row", background: "#fff" }}>
      <FormKitStyles />

      {/* Left: value proposition */}
      <div style={{
        flex: isMobile ? "none" : "0 0 42%",
        background: C.bgDarkAlt, color: C.textOnDark,
        padding: isMobile ? "36px 24px 32px" : "64px 56px",
        display: "flex", flexDirection: "column", justifyContent: isMobile ? "flex-start" : "center",
        position: "relative",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: isMobile ? 24 : 56, cursor: "pointer" }} onClick={onBack}>
          <AppLogo size={26} />
          <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-.2px" }}>SDR ReachOut</span>
        </div>

        <h1 style={{ fontSize: isMobile ? 28 : "clamp(30px, 3vw, 40px)", fontWeight: 700, letterSpacing: "-1px", lineHeight: 1.1, margin: "0 0 14px" }}>
          Start Your<br />7-Day Free Trial
        </h1>
        <p style={{ fontSize: 15.5, color: C.textSubOnDark, lineHeight: 1.6, margin: "0 0 32px", maxWidth: 420 }}>
          Everything you need to find, reach, and convert your next best customers — set up in under a minute.
        </p>

        {!isMobile && (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 14 }}>
            {VALUE_POINTS.map(pt => (
              <li key={pt} style={{ display: "flex", alignItems: "flex-start", gap: 12, fontSize: 14, color: C.textSubOnDark, lineHeight: 1.5 }}>
                <span style={{ flexShrink: 0, width: 18, height: 18, borderRadius: "50%", background: "rgba(255,255,255,.1)", display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#0a84ff" }} />
                </span>
                <span style={{ color: "#f5f5f7" }}>{pt}</span>
              </li>
            ))}
          </ul>
        )}

        {!isMobile && (
          <div style={{ marginTop: 48, paddingTop: 28, borderTop: "1px solid rgba(255,255,255,.1)", fontSize: 12.5, color: C.textFaint }}>
            No credit card required · Cancel anytime
          </div>
        )}
      </div>

      {/* Right: signup form */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? "32px 20px 48px" : "56px 40px" }}>
        <form onSubmit={handleSubmit} style={{ width: "100%", maxWidth: 440 }}>
          <div style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: "-.4px", margin: "0 0 6px" }}>Create your account</h2>
            <p style={{ fontSize: 14, color: C.textSub, margin: 0 }}>
              Already have one?{" "}
              <button type="button" onClick={onLogin} style={{ background: "none", border: "none", padding: 0, color: C.accent, fontWeight: 600, cursor: "pointer", fontSize: 14, fontFamily: FONT }}>
                Log in
              </button>
            </p>
          </div>

          {serverError && (
            <div style={{ background: C.redSoft, border: `1px solid rgba(224,57,62,.25)`, borderRadius: 12, padding: "12px 14px", marginBottom: 20 }}>
              <ValidationMessage>{serverError}</ValidationMessage>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <TextField id="firstName" label="First name" placeholder="Jordan" value={fields.firstName} onChange={set("firstName")} onBlur={blur("firstName")} error={err("firstName")} autoComplete="given-name" />
            <TextField id="lastName" label="Last name" placeholder="Blake" value={fields.lastName} onChange={set("lastName")} onBlur={blur("lastName")} error={err("lastName")} autoComplete="family-name" />
          </div>

          <div style={{ marginBottom: 14 }}>
            <TextField id="email" type="email" label="Work email" placeholder="you@company.com" value={fields.email} onChange={set("email")} onBlur={blur("email")} error={err("email")} autoComplete="email" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <TextField id="companyName" label="Company name" placeholder="Acme Inc." value={fields.companyName} onChange={set("companyName")} onBlur={blur("companyName")} error={err("companyName")} autoComplete="organization" />
            <TextField id="jobTitle" label="Job title" optional placeholder="Head of Sales" value={fields.jobTitle} onChange={set("jobTitle")} autoComplete="organization-title" />
          </div>

          <div style={{ marginBottom: 14 }}>
            <TextField id="companyWebsite" label="Company website" optional placeholder="acme.com" value={fields.companyWebsite} onChange={set("companyWebsite")} autoComplete="url" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 8 }}>
            <PasswordField id="password" label="Password" placeholder="At least 8 characters" value={fields.password} onChange={set("password")} onBlur={blur("password")} error={err("password")} autoComplete="new-password" />
            <PasswordField id="confirmPassword" label="Confirm password" placeholder="Re-enter password" value={fields.confirmPassword} onChange={set("confirmPassword")} onBlur={blur("confirmPassword")} error={err("confirmPassword")} autoComplete="new-password" />
          </div>

          <Button type="submit" size="lg" loading={submitting} style={{ width: "100%", marginTop: 22 }}>
            {submitting ? "Creating your account…" : <>Start My 7-Day Free Trial &rarr;</>}
          </Button>

          <p style={{ textAlign: "center", fontSize: 12.5, color: C.textFaint, margin: "16px 0 0" }}>
            No credit card required. Cancel anytime.
          </p>
          <p style={{ textAlign: "center", fontSize: 11.5, color: C.textFaint, margin: "10px 0 0", lineHeight: 1.5 }}>
            By continuing, you agree to SDR ReachOut's <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: C.accent, fontWeight: 600 }}>Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: C.accent, fontWeight: 600 }}>Privacy Policy</a>.
          </p>
        </form>
      </div>
    </div>
  );
}
